import type { TFunction } from 'i18next';

import { formatEUR } from './tool.fn';
import type { PaymentQueueItem } from '../models/payment-queue.entity';

/**
 * 将付款队列金额格式化为欧元展示文案。
 * @param raw 原始金额
 */
export function formatPaymentQueueAmount(raw: string | number | null | undefined): string {
    if (raw === null || raw === undefined || raw === '') {
        return formatEUR(0);
    }
    const n = Number(raw);
    return formatEUR(Number.isNaN(n) ? 0 : n);
}

/**
 * 支付状态文案（0 未付 / 1 已付 / 2 退款，与 POS 一致）。
 * @param status payment_status
 * @param t i18n
 */
export function formatPaymentQueueStatus(status: number | null | undefined, t: TFunction): string {
    if (status === 0) {
        return t('order_detail_payment_unpaid');
    }
    if (status === 1) {
        return t('order_detail_payment_paid');
    }
    if (status === 2) {
        return t('manager_text_summary_payment_details_refund');
    }
    return t('immediate_unknown');
}

/**
 * 是否开票文案（0 否 / 1 是）。
 * @param flag is_create_invoice
 * @param t i18n
 */
export function formatPaymentQueueInvoiceFlag(flag: number | null | undefined, t: TFunction): string {
    if (flag === 0) {
        return t('general_text_no');
    }
    if (flag === 1) {
        return t('general_text_yes');
    }
    return t('immediate_unknown');
}

/** 付款队列展示行配置 */
export type PaymentQueueRowConfig = {
    /** i18n label key */
    labelKey: string;
    /** 从队列项解析展示值 */
    getValue: (item: PaymentQueueItem, t: TFunction) => string;
};

/**
 * 生成付款队列列表项 React key；混合支付展开后同 queue.id 可能多行，须带上 index。
 * @param item - 付款队列行
 * @param index - 当前列表下标
 * @param prefix - key 前缀，默认 `payment-queue`
 */
export function getPaymentQueueRowKey(
    item: PaymentQueueItem,
    index: number,
    prefix = 'payment-queue'
): string {
    const idPart = item.id != null ? String(item.id) : 'noid';
    return `${prefix}-${index}-${idPart}`;
}

/** 付款队列行（字段顺序与 POS history-orders-page 一致） */
export const PAYMENT_QUEUE_ROW_CONFIG: PaymentQueueRowConfig[] = [
    {
        labelKey: 'order_detail_payment_queue_id',
        getValue: (item) => (item.id != null ? String(item.id) : '–'),
    },
    {
        labelKey: 'order_detail_payment_type',
        getValue: (item) => item.payment_type_name?.trim() || '–',
    },
    {
        labelKey: 'order_detail_payment_status',
        getValue: (item, t) => formatPaymentQueueStatus(item.payment_status, t),
    },
    {
        labelKey: 'order_detail_payment_amount',
        getValue: (item) => formatPaymentQueueAmount(item.discounted_total_price),
    },
    {
        labelKey: 'order_detail_payment_tip',
        getValue: (item) => formatPaymentQueueAmount(item.tip_amount),
    },
    {
        labelKey: 'order_detail_payment_service_fee',
        getValue: (item) => formatPaymentQueueAmount(item.service_amount),
    },
    {
        labelKey: 'order_detail_payment_is_invoice',
        getValue: (item, t) => formatPaymentQueueInvoiceFlag(item.is_create_invoice, t),
    },
];
